import React from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { useBookingDraft } from "../BookingDraftContext";
import { joinWaitlist } from "@/lib/services/waitlistPublicService";
import { getCompanyRegistry } from "@/lib/company/companyRegistrySingleton";
import {
  validateName,
  validatePhone,
  validateOptionalEmail,
  contactErrorMessage,
  phoneCodeExample,
} from "@/lib/validation/contact";

/**
 * Waiting-list join panel (waitlist README §4.1) — shown on the calendar
 * step when no suitable time exists (horizon / empty-day triggers, both
 * computed in DateTimeView.container). Contact fields ride the booking
 * draft; email present = one-tap auto-notify opt-in.
 */
export default function WaitlistPanel({
  serviceId,
  staffId,
  defaultFrom,
  defaultTo,
  collapsible = false,
}: {
  serviceId: number;
  staffId: number | null;
  defaultFrom: string;
  defaultTo: string;
  /**
   * When true the form starts collapsed behind a tappable heading — used
   * for the inline calendar panel, where an auto-expanded form was mistaken
   * for the appointment booking itself. Inside the dedicated waiting-list
   * dialog it stays expanded (the dialog is already an explicit opt-in).
   */
  collapsible?: boolean;
}) {
  const { t } = useTranslation();
  const { draft, actions } = useBookingDraft();
  const [dateFrom, setDateFrom] = React.useState(defaultFrom);
  const [dateTo, setDateTo] = React.useState(defaultTo);
  const [timePref, setTimePref] = React.useState<"any" | "morning" | "afternoon" | "evening">("any");
  const [state, setState] = React.useState<"idle" | "busy" | "done" | "duplicate" | "error">("idle");
  const [errorMsg, setErrorMsg] = React.useState("");
  // Collapsible mode starts closed so users don't mistake the form for the
  // actual appointment booking; otherwise it's open with no toggle.
  const [open, setOpen] = React.useState(!collapsible);

  const prefs = [
    ["any", t("widget.waitlist.pref_any", "Any time")],
    ["morning", t("widget.waitlist.pref_morning", "Morning")],
    ["afternoon", t("widget.waitlist.pref_afternoon", "Afternoon")],
    ["evening", t("widget.waitlist.pref_evening", "Evening")],
  ] as const;

  const submit = async () => {
    // Same contact validation as the appointment review screen (shared —
    // @/lib/validation/contact): name required, phone must start with the
    // country code, email optional but well-formed. Show the first error.
    const contactErr =
      validateName(draft.contact?.name) ??
      validatePhone(draft.contact?.phone) ??
      validateOptionalEmail(draft.contact?.email);
    if (contactErr) {
      const phoneExample = phoneCodeExample(getCompanyRegistry().getSettings()?.phone_code);
      setErrorMsg(contactErrorMessage(contactErr, t, { phoneExample }));
      setState("error");
      return;
    }
    setState("busy");
    setErrorMsg("");
    try {
      await joinWaitlist({
        client_name: draft.contact.name,
        client_phone: draft.contact.phone,
        client_email: draft.contact.email || undefined,
        service_id: serviceId,
        staff_id: staffId,
        date_from: dateFrom,
        date_to: dateTo,
        time_pref: timePref,
      });
      setState("done");
    } catch (e: unknown) {
      const ax = e as import("axios").AxiosError<any>;
      if (ax?.response?.status === 409) {
        setState("duplicate");
      } else {
        setErrorMsg(String(ax?.response?.data?.error ?? t("widget.waitlist.failed", "Could not join the waiting list")));
        setState("error");
      }
    }
  };

  if (state === "done") {
    return (
      <Card className="mt-4 border-emerald-300 bg-emerald-50 p-4">
        <div className="font-semibold text-emerald-800">
          {t("widget.waitlist.joined_title", "You're on the waiting list")}
        </div>
        <p className="mt-1 text-sm text-emerald-800">
          {draft.contact?.email
            ? t("widget.waitlist.joined_email", "The moment a matching time frees up we'll email you a direct link — one tap to book it.")
            : t("widget.waitlist.joined_no_email", "The salon will contact you when a matching time frees up.")}
        </p>
      </Card>
    );
  }

  if (state === "duplicate") {
    return (
      <Card className="mt-4 border-amber-300 bg-amber-50 p-4 text-sm text-amber-800">
        {t("widget.waitlist.duplicate", "You're already on the waiting list for this service in this period.")}
      </Card>
    );
  }

  return (
    <Card className="mt-4 border-dashed p-4">
      {collapsible ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex w-full items-center justify-between gap-2 text-left font-semibold"
        >
          <span>{t("widget.waitlist.title", "No openings soon?")}</span>
          <ChevronDown
            className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </button>
      ) : (
        <div className="font-semibold">
          {t("widget.waitlist.title", "No openings soon?")}
        </div>
      )}

      {!open ? null : (
      <>
      <p className="mt-1 text-sm text-muted-foreground">
        {t("widget.waitlist.intro", "Join the waiting list — if a spot frees up we'll email you a direct link to book it in one tap.")}
      </p>

      <div className="mt-3 grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-muted-foreground">{t("widget.waitlist.from", "From")}</label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">{t("widget.waitlist.to", "To")}</label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
      </div>

      <div className="mt-2 flex flex-wrap gap-2">
        {prefs.map(([value, label]) => (
          <Button key={value} size="sm" variant={timePref === value ? "default" : "outline"}
                  className="h-8 rounded-full" onClick={() => setTimePref(value)}>
            {label}
          </Button>
        ))}
      </div>

      <div className="mt-3 grid gap-2">
        <Input placeholder={t("widget.waitlist.name", "Your name")}
               value={draft.contact?.name ?? ""}
               onChange={(e) => actions.setClientName(e.target.value)} />
        <Input placeholder={t("widget.waitlist.phone", "Phone (+.....)")}
               value={draft.contact?.phone ?? ""}
               onChange={(e) => actions.setClientPhone(e.target.value)} />
        <Input placeholder={t("widget.waitlist.email", "Email — for the one-tap booking link (optional)")}
               value={draft.contact?.email ?? ""}
               onChange={(e) => actions.setClientEmail(e.target.value)} />
      </div>

      {state === "error" && (
        <p className="mt-2 text-sm text-destructive">{errorMsg}</p>
      )}

      <Button className="mt-3 w-full rounded-xl font-semibold" disabled={state === "busy"} onClick={submit}>
        {t("widget.waitlist.join", "Join the waiting list")}
      </Button>
      </>
      )}
    </Card>
  );
}
